// The one door every board widget uses to reach data.
//
// On a real board these are Firestore's own functions and the network,
// unchanged. In preview mode (the admin dashboard's Station Board Studio
// showing a template before anyone builds it) they are served from an
// in-memory fake agency instead, so the Studio can show the real widgets —
// the exact components a TV runs — filled with believable data, without a
// login and without touching any real agency's records.
//
// Widgets import from here rather than from "firebase/firestore" / "fetch" /
// "firebase/functions" directly; that is the whole seam.

import * as fs from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db as realDb, functions } from "../firebase";

let demo = null; // { collections: { "path": [{ id, ...data }] }, docs: { "path": data }, fetch(url), call(name, data) }

export function enableDemoData(fixtures) {
  demo = fixtures;
}
export function isDemoData() {
  return demo !== null;
}

// Screenshot capture asks for a "still" preview: animations that would make a
// capture depend on timing (the radar loop) hold on their final frame.
let still = false;
export function setPreviewStill(value) {
  still = !!value;
}
export function isPreviewStill() {
  return still;
}

export const db = realDb;

// Preview only: fake reflectivity (a GeoJSON FeatureCollection) for the storm
// map, `minutesAgo` minutes back. Null on a real board, which uses live radar.
export function demoRadar(minutesAgo) {
  return demo && demo.radar ? demo.radar(minutesAgo) : null;
}

// ---- refs -------------------------------------------------------------------

const joinPath = (segments) => segments.map(String).join("/");

export function collection(dbArg, ...segments) {
  if (!demo) return fs.collection(dbArg, ...segments);
  return { __demo: "collection", path: joinPath(segments), constraints: [] };
}

export function doc(dbArg, ...segments) {
  if (!demo) return fs.doc(dbArg, ...segments);
  return { __demo: "doc", path: joinPath(segments) };
}

export function where(field, op, value) {
  if (!demo) return fs.where(field, op, value);
  return { field, op, value };
}

export function query(ref, ...constraints) {
  if (!demo) return fs.query(ref, ...constraints);
  return { ...ref, constraints: [...(ref.constraints || []), ...constraints] };
}

// ---- reads ------------------------------------------------------------------

function matches(row, { field, op, value }) {
  const v = row[field];
  switch (op) {
    case "==": return v === value;
    case "!=": return v !== value;
    case "in": return Array.isArray(value) && value.includes(v);
    case "array-contains": return Array.isArray(v) && v.includes(value);
    case ">": return v > value;
    case ">=": return v >= value;
    case "<": return v < value;
    case "<=": return v <= value;
    default: return true;
  }
}

function demoQuerySnapshot(ref) {
  const rows = (demo.collections[ref.path] || []).filter((row) =>
    (ref.constraints || []).every((c) => matches(row, c))
  );
  const docs = rows.map(({ id, ...data }) => ({ id, data: () => data, exists: () => true }));
  return { docs, size: docs.length, empty: docs.length === 0, forEach: (fn) => docs.forEach(fn) };
}

function demoDocSnapshot(ref) {
  const data = demo.docs[ref.path];
  const id = ref.path.split("/").pop();
  return { id, exists: () => data !== undefined, data: () => data };
}

function demoSnapshot(ref) {
  return ref.__demo === "doc" ? demoDocSnapshot(ref) : demoQuerySnapshot(ref);
}

export function onSnapshot(ref, onNext, onError) {
  if (!demo) return fs.onSnapshot(ref, onNext, onError);
  // Asynchronous like the real listener, so widgets see the same render order.
  const t = setTimeout(() => onNext(demoSnapshot(ref)), 0);
  return () => clearTimeout(t);
}

export async function getDoc(ref) {
  if (!demo) return fs.getDoc(ref);
  return demoDocSnapshot(ref);
}

export async function getDocs(ref) {
  if (!demo) return fs.getDocs(ref);
  return demoQuerySnapshot(ref);
}

// ---- network and callables ----------------------------------------------------

// fetch() for third-party feeds (weather, NWS alerts). Returns a Response-like
// object in preview mode.
export async function boardFetch(url, options) {
  if (!demo) return fetch(url, options);
  const body = demo.fetch ? demo.fetch(String(url)) : undefined;
  return {
    ok: body !== undefined,
    status: body !== undefined ? 200 : 404,
    json: async () => body ?? {},
  };
}

// httpsCallable(functions, name)(data), resolved to { data } like the SDK.
export async function callFunction(name, data) {
  if (!demo) return httpsCallable(functions, name)(data);
  if (!demo.call) throw new Error(`No demo data for ${name}`);
  return { data: await demo.call(name, data) };
}
